import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useApp } from '../App';
import { TeamRoles, rolesFor } from '../utils/roles';

// Phase 1
import SchedulePanel from '../components/SchedulePanel';
// Phase 2
import ScheduleForm from '../components/ScheduleForm';
import DuesPanel from '../components/DuesPanel';
import BoardPanel from '../components/BoardPanel';
import MediaPanel from '../components/MediaPanel';
// Phase 3
import GameRecordPanel from '../components/GameRecordPanel';
import StatsPanel from '../components/StatsPanel';
import WorkoutPanel from '../components/WorkoutPanel';
// Phase 4
import PollPanel from '../components/PollPanel';
import IceRinkPanel from '../components/IceRinkPanel';
import GuestPanel from '../components/GuestPanel';
import MemberPanel from '../components/MemberPanel';
import ProfilePanel from '../components/ProfilePanel';
// Phase 6 - 캘린더
import CalendarPanel from '../components/CalendarPanel';

// ── 팀 없을 때 안내 화면 (방법 C) ────────────────────────────────────────────
function NoTeamScreen({ user, userProfile }) {
  return (
    <div style={nt.wrap}>
      <div style={nt.card}>
        <div style={nt.iconWrap}>
          <span style={{ fontSize: 52 }}>🏒</span>
        </div>
        <div style={nt.title}>팀 소속 후 이용 가능합니다</div>
        <div style={nt.subtitle}>
          안녕하세요, <strong style={{ color: '#5BC4D8' }}>{userProfile?.name || user?.email}</strong>님!<br />
          앱에서 팀을 만들거나 초대를 수락한 후<br />
          웹 대시보드를 이용하실 수 있습니다.
        </div>

        <div style={nt.appGuide}>
          <div style={nt.appGuideTitle}>📱 IonIce 앱에서 시작하세요</div>
          <div style={nt.appGuideText}>
            팀 생성 및 가입은 iOS / Android 앱에서만 가능합니다.<br />
            팀에 합류한 후 이 화면이 자동으로 업데이트됩니다.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <span style={nt.appBadge}>🍎 App Store</span>
            <span style={nt.appBadge}>🤖 Google Play</span>
          </div>
        </div>

        <div style={nt.accountInfo}>
          <span style={{ color: '#445566', fontSize: 12 }}>현재 계정</span>
          <span style={{ color: '#778899', fontSize: 12, fontWeight: 600 }}>
            {user?.email}
          </span>
        </div>
      </div>
    </div>
  );
}

const nt = {
  wrap: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: 'calc(100vh - 55px)', padding: '40px 24px',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(91,196,216,0.12)',
    borderRadius: 20, padding: '44px 40px',
    width: '100%', maxWidth: 440, textAlign: 'center',
    boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
  },
  iconWrap: {
    width: 88, height: 88, borderRadius: '50%',
    background: 'rgba(91,196,216,0.08)',
    border: '1px solid rgba(91,196,216,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  title: { fontSize: 22, fontWeight: 800, color: '#E8ECF1', marginBottom: 10 },
  subtitle: { fontSize: 14, color: '#778899', lineHeight: 1.8, marginBottom: 28 },
  appGuide: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, padding: '20px 24px', marginBottom: 20,
  },
  appGuideTitle: { fontSize: 14, fontWeight: 700, color: '#C8D8E8', marginBottom: 8 },
  appGuideText: { fontSize: 13, color: '#667788', lineHeight: 1.7 },
  appBadge: {
    padding: '7px 18px', borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    fontSize: 12, color: '#8899AA', fontWeight: 600,
  },
  accountInfo: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
  },
};


// ── Dashboard 메인 ─────────────────────────────────────────────────────────────
function Dashboard() {
  const app = useApp();
  const { user, userProfile, teams, currentTeam, navigate,
          switchTeam, loadUserData, roles, roleKey } = app;
  const uid = user?.uid;

  const [activeMenu, setActiveMenu]       = useState('schedule');
  const [sidebarOpen, setSidebarOpen]     = useState(true);
  const [showTeamSwitch, setShowTeamSwitch] = useState(false);
  const [copyScheduleData, setCopyScheduleData] = useState(null);

  // 팀을 바꾸거나 역할이 바뀌어서 현재 화면을 볼 수 없게 되면 일정으로 되돌린다.
  // (A팀 회원 / B팀 운영자처럼 팀마다 역할이 다를 수 있다.)
  useEffect(() => {
    const need = MENU_NEEDS[activeMenu];
    if (need && !roles[need]) setActiveMenu('schedule');
  }, [activeMenu, roles.isAdmin, roles.isStaff]);

  const handleCopySchedule = (schedule) => {
    setCopyScheduleData(schedule);
    setActiveMenu('createSchedule');
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('landing');
  };

  const role = TeamRoles[roleKey] || TeamRoles.none;
  const roleColor = role.color;
  const menuItems = getMenuItems(roles);

  const renderContent = () => {
    const need = MENU_NEEDS[activeMenu];
    if (need && !roles[need]) return <NoAccess need={need} />;
    switch (activeMenu) {
      case 'schedule':       return <SchedulePanel onCopySchedule={handleCopySchedule} />;
      case 'createSchedule': return <ScheduleForm onDone={() => { setActiveMenu('schedule'); setCopyScheduleData(null); }} copyFrom={copyScheduleData} />;
      case 'dues':           return <DuesPanel />;
      case 'board':          return <BoardPanel />;
      case 'media':          return <MediaPanel />;
      case 'gameRecord':     return <GameRecordPanel />;
      case 'stats':          return <StatsPanel />;
      case 'workout':        return <WorkoutPanel />;
      case 'poll':           return <PollPanel />;
      case 'iceRink':        return <IceRinkPanel />;
      case 'guest':          return <GuestPanel />;
      case 'members':        return <MemberPanel />;
      case 'profile':        return <ProfilePanel />;
      case 'calendar':       return <CalendarPanel />;
      default:               return <SchedulePanel />;
    }
  };

  return (
    <div style={s.root}>
      <link href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Noto+Sans+KR:wght@300;400;500;700&display=swap" rel="stylesheet" />

      {/* Mode color line */}
      <div style={{ height: 3, backgroundColor: roleColor }} />

      {/* Top bar */}
      <header style={s.topBar}>
        <div style={s.topBarLeft}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={s.menuBtn}>☰</button>
          <div style={s.logoGroup} onClick={() => navigate('landing')}>
            <span>🏒</span>
            <span style={s.logoText}>IonIce</span>
          </div>
          {currentTeam && (
            <span onClick={() => setShowTeamSwitch(true)} style={{ ...s.teamBadge, cursor: 'pointer' }}>
              {currentTeam.name} ▾
            </span>
          )}
          {currentTeam && (
            <span style={{ ...s.modeBadge, backgroundColor: roleColor }}
                  title="이 팀에서의 역할 — 팀 문서의 소유자·운영자·코치 명단으로 정해집니다">
              {role.icon} {role.label}
            </span>
          )}
        </div>

        <div style={s.topBarRight}>
          <span style={{ color: '#778899', fontSize: 13 }}>{userProfile?.name || user?.email}</span>
          <button onClick={handleLogout} style={s.logoutBtn}>로그아웃</button>
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1 }}>
        {/* Sidebar */}
        {sidebarOpen && currentTeam && (
          <aside style={s.sidebar}>
            {menuItems.map((group, gi) => (
              <div key={gi} style={{ padding: '12px 0' }}>
                <div style={s.sideLabel}>{group.label}</div>
                {group.items.map(item => (
                  <div
                    key={item.key}
                    onClick={() => setActiveMenu(item.key)}
                    style={{
                      ...s.sideItem,
                      backgroundColor: activeMenu === item.key ? 'rgba(91,196,216,0.1)' : 'transparent',
                      borderLeft: activeMenu === item.key ? `3px solid ${roleColor}` : '3px solid transparent',
                    }}
                  >
                    <span style={{ marginRight: 8 }}>{item.icon}</span>
                    <span style={{ fontSize: 13 }}>{item.label}</span>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(91,196,216,0.06)', marginTop: 'auto' }}>
              <span onClick={() => navigate('landing')} style={{ color: '#667788', fontSize: 12, cursor: 'pointer' }}>← 홈으로</span>
            </div>
          </aside>
        )}

        {/* Main content */}
        <main style={{ flex: 1, padding: currentTeam ? 24 : 0, overflow: 'auto', minHeight: 'calc(100vh - 55px)' }}>
          {currentTeam
            ? renderContent()
            : <NoTeamScreen user={user} userProfile={userProfile} />
          }
        </main>
      </div>


      {/* 팀 변경 모달 */}
      {showTeamSwitch && (
        <div className="modal-overlay" onClick={() => setShowTeamSwitch(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h3 style={{ margin: '0 0 20px', color: '#5BC4D8' }}>팀 변경</h3>
            {teams.map(team => (
              <div key={team.id} onClick={() => { switchTeam(team); setShowTeamSwitch(false); }}
                style={{ ...s.switchItem, backgroundColor: currentTeam?.id === team.id ? 'rgba(91,196,216,0.1)' : 'transparent' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{team.name}</span>
                  {(() => {
                    const r = TeamRoles[rolesFor(team, uid).roleKey] || TeamRoles.none;
                    return (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 9,
                        color: r.color, border: `1px solid ${r.color}55`,
                      }}>{r.icon} {r.label}</span>
                    );
                  })()}
                </span>
                {currentTeam?.id === team.id && <span style={{ color: '#5BC4D8' }}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// 메뉴는 팀 문서로 계산한 역할만 본다. (예전에는 사용자가 고르는 "모드"로 나뉘어서
// 일반 회원이 운영진 모드를 골라 회비·멤버·게스트 화면을 열 수 있었다 — W1-02)
function getMenuItems(roles) {
  const groups = [
    { label: '일정', items: [
      { key: 'schedule', icon: '📅', label: '일정 관리' },
      { key: 'calendar', icon: '🗓️', label: '캘린더' },
      ...(roles.isStaff ? [{ key: 'createSchedule', icon: '➕', label: '일정 등록' }] : []),
    ]},
    { label: '활동', items: [
      { key: 'gameRecord', icon: '🏒', label: '경기 기록' },
      { key: 'stats', icon: '📊', label: '통계' },
    ]},
    { label: '소통', items: [
      { key: 'board', icon: '📋', label: '게시판' },
      { key: 'poll', icon: '🗳️', label: '투표' },
      { key: 'media', icon: '📷', label: '미디어' },
    ]},
  ];

  // 운영진(소유자·운영자·코치)
  if (roles.isStaff) {
    groups.push({ label: '코치', items: [
      { key: 'workout', icon: '🏋️', label: '훈련 프로그램' },
    ]});
  }

  // 운영자(소유자·운영자)만 — 규칙이 코치를 거부하는 것들
  if (roles.isAdmin) {
    groups.push({ label: '관리', items: [
      { key: 'dues', icon: '💰', label: '회비 관리' },
      { key: 'members', icon: '👥', label: '멤버 관리' },
      { key: 'guest', icon: '🎫', label: '게스트 승인' },
    ]});
  }

  groups.push({ label: '더보기', items: [
    { key: 'iceRink', icon: '🏟️', label: '아이스링크' },
    { key: 'profile', icon: '👤', label: '프로필' },
  ]});

  return groups;
}

// 메뉴를 숨기는 것만으로는 막히지 않으므로(주소·상태 조작) 화면 자체도 막는다.
const MENU_NEEDS = {
  createSchedule: 'isStaff',
  workout:        'isStaff',
  dues:           'isAdmin',
  members:        'isAdmin',
  guest:          'isAdmin',
};

function NoAccess({ need }) {
  const what = need === 'isAdmin' ? '팀 소유자 또는 운영자' : '팀 운영진(소유자·운영자·코치)';
  return (
    <div style={{ padding: '60px 24px', textAlign: 'center', color: '#667788' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#8899AA', marginBottom: 8 }}>
        {what}만 볼 수 있는 화면입니다
      </div>
      <div style={{ fontSize: 13 }}>
        역할은 팀 문서의 소유자·운영자·코치 명단으로 정해집니다. 팀 운영자에게 문의해 주세요.
      </div>
    </div>
  );
}

const s = {
  root: { fontFamily: "'Noto Sans KR', sans-serif", backgroundColor: '#0A1220', color: '#E8ECF1', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 24px', backgroundColor: 'rgba(10,18,32,0.95)', borderBottom: '1px solid rgba(91,196,216,0.08)', backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 100 },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  topBarRight: { display: 'flex', alignItems: 'center', gap: 12 },
  menuBtn: { background: 'none', border: 'none', color: '#8899AA', fontSize: 20, cursor: 'pointer', padding: '4px 8px' },
  logoGroup: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' },
  logoText: { fontSize: 20, fontWeight: 700, color: '#5BC4D8', fontFamily: "'Rajdhani', sans-serif", letterSpacing: 2 },
  teamBadge: { padding: '4px 12px', borderRadius: 12, fontSize: 12, backgroundColor: 'rgba(91,196,216,0.1)', color: '#5BC4D8', fontWeight: 600 },
  modeBadge: { padding: '4px 12px', borderRadius: 12, fontSize: 11, color: '#fff', fontWeight: 600 },
  logoutBtn: { padding: '6px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 12, cursor: 'pointer', fontWeight: 500 },
  sidebar: { width: 220, minHeight: 'calc(100vh - 55px)', backgroundColor: 'rgba(255,255,255,0.01)', borderRight: '1px solid rgba(91,196,216,0.06)', display: 'flex', flexDirection: 'column', overflow: 'auto' },
  sideLabel: { padding: '8px 20px', fontSize: 11, color: '#5BC4D8', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' },
  sideItem: { padding: '10px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.15s' },
  switchItem: { padding: '12px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
};

export default Dashboard;
